import { Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { passwordRules } from '@/utils/validation/rules';

interface PasswordRequirementsProps {
  password: string;
  className?: string;
}

interface RequirementItemProps {
  met: boolean;
  label: string;
}

function RequirementItem({ met, label }: RequirementItemProps) {
  return (
    <li className="flex items-center gap-2">
      {met ? (
        <Check className="h-4 w-4 text-green-500" aria-hidden="true" />
      ) : (
        <X className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
      )}
      <span className={cn('text-sm', met ? 'text-green-600' : 'text-muted-foreground')}>
        {label}
      </span>
    </li>
  );
}

export function PasswordRequirements({ password, className }: PasswordRequirementsProps) {
  const requirements = [
    { met: passwordRules.minLength().validate(password), label: 'At least 8 characters' },
    { met: passwordRules.hasUppercase().validate(password), label: 'One uppercase letter' },
    { met: passwordRules.hasLowercase().validate(password), label: 'One lowercase letter' },
    { met: passwordRules.hasNumber().validate(password), label: 'One number' },
    { met: passwordRules.hasSpecialChar().validate(password), label: 'One special character' },
  ];

  return (
    <div
      className={cn('rounded-lg bg-muted p-4', className)}
      role="status"
      aria-label="Password requirements"
    >
      <h3 className="text-sm font-medium mb-2">Password Requirements:</h3>
      <ul className="space-y-1" aria-live="polite">
        {requirements.map((req) => (
          <RequirementItem key={req.label} met={req.met} label={req.label} />
        ))}
      </ul>
    </div>
  );
}
